import { AddProfileCard } from '@/components/billing/AddProfileCard';
import { BillingSummaryCard } from '@/components/billing/BillingSummaryCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useIndividualsApi } from '@/hooks/useIndividualsApi';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, CreditCard, ExternalLink, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const statusConfig: Record<
  string,
  { color: string; icon: typeof CheckCircle; label: string; description: string }
> = {
  active: {
    color: 'bg-green-500/10 text-green-500 border-green-500/20',
    icon: CheckCircle,
    label: 'Active',
    description: 'Your subscription is active and in good standing.',
  },
  pending: {
    color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    icon: AlertCircle,
    label: 'Pending',
    description: 'Your subscription is pending activation. Please complete payment.',
  },
  inactive: {
    color: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
    icon: AlertCircle,
    label: 'Inactive',
    description: 'No active subscription. Subscribe to access all features.',
  },
  past_due: {
    color: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    icon: AlertCircle,
    label: 'Past Due',
    description: 'Your payment is past due. Please update your payment method.',
  },
  cancelled: {
    color: 'bg-red-500/10 text-red-500 border-red-500/20',
    icon: XCircle,
    label: 'Cancelled',
    description: 'Your subscription has been cancelled. Resubscribe to continue access.',
  },
};

export default function IndividualBilling() {
  const individualsApi = useIndividualsApi();
  const { data, isLoading, error } = useQuery({
    queryKey: ['individual-dashboard'],
    queryFn: async () => {
      const response = await individualsApi.getDashboard();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const billingPortalMutation = useMutation({
    mutationFn: async () => {
      const response = await individualsApi.getBillingPortal();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to open billing portal');
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      const response = await individualsApi.createCheckoutSession();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (data) => {
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
    },
  });

  const handleManageBilling = () => {
    billingPortalMutation.mutate();
  };

  const handleSubscribe = () => {
    checkoutMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load billing information</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  const status = data?.user?.subscription_status || 'inactive';
  const defaultConfig = {
    color: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
    icon: AlertCircle,
    label: 'Inactive',
    description: 'No active subscription. Subscribe to access all features.',
  };
  const config = statusConfig[status] ?? defaultConfig;
  const StatusIcon = config?.icon ?? AlertCircle;
  const configColor = config?.color ?? defaultConfig.color;
  const configLabel = config?.label ?? defaultConfig.label;
  const configDescription = config?.description ?? defaultConfig.description;

  return (
    <>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Billing</h1>
        <p className="mt-1 text-muted-foreground">Manage your subscription and payment details.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Subscription Status */}
        <Card className="animate-fade-in">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Subscription Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start gap-4">
              <div
                className={`rounded-full p-3 ${
                  status === 'active'
                    ? 'bg-green-500/10'
                    : status === 'cancelled'
                      ? 'bg-red-500/10'
                      : 'bg-yellow-500/10'
                }`}
              >
                <StatusIcon
                  className={`h-6 w-6 ${
                    status === 'active'
                      ? 'text-green-500'
                      : status === 'cancelled'
                        ? 'text-red-500'
                        : 'text-yellow-500'
                  }`}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className={configColor}>
                    {configLabel}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{configDescription}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-4 border-t border-border">
              {status === 'active' || status === 'past_due' ? (
                <Button
                  onClick={handleManageBilling}
                  disabled={billingPortalMutation.isPending}
                  className="w-full"
                >
                  {billingPortalMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4 mr-2" />
                  )}
                  Manage Subscription
                </Button>
              ) : (
                <Button
                  onClick={handleSubscribe}
                  disabled={checkoutMutation.isPending}
                  className="w-full"
                >
                  {checkoutMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  {status === 'cancelled' ? 'Resubscribe' : 'Subscribe Now'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Plan Details */}
        <Card className="animate-fade-in" style={{ animationDelay: '50ms' }}>
          <CardHeader>
            <CardTitle>ErgShoot Individual Plan</CardTitle>
            <CardDescription>Personal access to ErgShoot programming</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-3xl font-bold text-primary">
              $6.75<span className="text-lg text-muted-foreground font-normal">/month</span>
            </div>

            <ul className="space-y-3">
              {[
                'Access to all ErgShoot programming',
                'Personal workout tracking',
                'Score logging and history',
                'Leaderboard participation',
                'Mobile-friendly interface',
              ].map((feature, index) => (
                <li
                  key={feature}
                  className="flex items-center gap-2 text-sm animate-fade-in"
                  style={{ animationDelay: `${100 + index * 30}ms` }}
                >
                  <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            {status !== 'active' && (
              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Cancel anytime. No long-term commitment required.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* STORY-STRIPE-002 — In-App Billing Summary */}
        <div className="lg:col-span-2">
          <BillingSummaryCard scope="individual" />
        </div>

        {/* STORY-STRIPE-009a — life-stage upgrade: individual → also owner */}
        <div className="lg:col-span-2 animate-fade-in" style={{ animationDelay: '50ms' }}>
          <AddProfileCard variant="add_owner" />
        </div>

        {/* Help Card */}
        <Card className="lg:col-span-2 animate-fade-in" style={{ animationDelay: '100ms' }}>
          <CardHeader>
            <CardTitle className="text-base">Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              If you have questions about billing or need to update your payment information, you
              can manage everything through the Stripe billing portal. For other inquiries, contact
              us at{' '}
              <a href="mailto:support@ergshoot.com" className="text-primary hover:underline">
                support@ergshoot.com
              </a>
              .
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
